import React, { useState } from 'react';
import { Mail, Check, AlertCircle } from 'lucide-react';

export default function Home() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [message, setMessage] = useState('');

  const handleSubscribe = async (e) => {
    e.preventDefault();

    if (!email) {
      setStatus('error');
      setMessage('Zadejte prosím email');
      return;
    }

    setStatus('loading');

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (data.success) {
        setStatus('success');
        setMessage('Skvěle! Zkontrolujte email pro potvrzení odběru.');
        setEmail('');
      } else {
        setStatus('error');
        setMessage(data.error || 'Něco se pokazilo. Zkuste to prosím znovu.');
      }
    } catch (error) {
      setStatus('error');
      setMessage('Chyba připojení. Zkuste to prosím znovu.');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="max-w-4xl mx-auto px-4 py-16 md:py-24">
        {/* Avatar a Název */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <img
              src="/avatar.jpg"
              alt="Technikálie"
              className="w-24 h-24 md:w-32 md:h-32 rounded-full object-cover shadow-lg"
            />
          </div>

          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Technikálie
          </h1>

          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Každý den se v technice něco děje. <strong>Technikálie</strong> připravuje Alex a Marsa
            pro Patricka Zandla každý den. Jsou to zkušení AI agenti, kteří prošmejdí internet
            a vyberou to, co stojí za pozornost a pozdější rozpracování do Patrickova Newsletteru.
            Nyní je můžete číst i vy!
          </p>
        </div>

        {/* Subscribe Form */}
        <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-8 md:p-12 shadow-xl">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-6">
            Přihlaste se k odběru
          </h2>

          <form onSubmit={handleSubscribe} className="max-w-md mx-auto">
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="vas@email.cz"
                disabled={status === 'loading'}
                className="flex-1 px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all disabled:bg-gray-100"
              />
              <button
                type="submit"
                disabled={status === 'loading'}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {status === 'loading' ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                    <span>Odesílám...</span>
                  </>
                ) : (
                  <>
                    <Mail size={20} />
                    <span>Odebírat</span>
                  </>
                )}
              </button>
            </div>

            {/* Status Messages */}
            {status === 'success' && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
                <Check className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
                <p className="text-green-800 text-sm">{message}</p>
              </div>
            )}

            {status === 'error' && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                <p className="text-red-800 text-sm">{message}</p>
              </div>
            )}
          </form>

          <p className="text-center text-sm text-gray-500 mt-6">
            Žádný spam. Odhlásit se můžete kdykoliv jedním kliknutím.
          </p>
        </div>

        {/* Features */}
        <div className="grid md:grid-cols-3 gap-6 mt-16">
          <Feature
            emoji="🤖"
            title="AI Kurátorování"
            description="Alex a Marsa denně analyzují tisíce článků a vyberou to nejzajímavější"
          />
          <Feature
            emoji="📧"
            title="Každý den"
            description="Čerstvé tech novinky přímo do vaší emailové schránky"
          />
          <Feature
            emoji="⚡"
            title="Pro Patricka Zandla"
            description="Připraveno speciálně pro Patrickův Newsletter"
          />
        </div>

        {/* Footer */}
        <div className="mt-20 pt-8 border-t border-gray-200 text-center">
          <p className="text-sm text-gray-500">
            © 2025 Technikálie • Powered by AI
          </p>
        </div>
      </div>
    </div>
  );
}

function Feature({ emoji, title, description }) {
  return (
    <div className="text-center p-6">
      <div className="text-4xl mb-3">{emoji}</div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-600 text-sm">{description}</p>
    </div>
  );
}
